import {
  IDGOV_CONFIG,
  SESSION_STORAGE_KEY,
  IDGOV_VALIDATION,
  AUTH_STATE_MAX_AGE_MS,
} from "./config";
import { generateOAuthState, validateOAuthState } from "../utils";
import type { AuthState, OAuthCallbackParams, IdGovUser } from "./types";

// Token storage keys
const ACCESS_TOKEN_KEY = "idgov_access_token";
const REFRESH_TOKEN_KEY = "idgov_refresh_token";
const TOKEN_EXPIRY_KEY = "idgov_token_expiry";

// Token management functions
export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

export function setTokens(accessToken: string, refreshToken: string, expiresIn: number): void {
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  localStorage.setItem(TOKEN_EXPIRY_KEY, String(Date.now() + expiresIn));
}

export function clearTokens(): void {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(TOKEN_EXPIRY_KEY);
}

export function isTokenExpired(): boolean {
  const expiry = localStorage.getItem(TOKEN_EXPIRY_KEY);
  if (!expiry) return true;
  // Consider token expired 30 seconds before actual expiry
  return Date.now() > parseInt(expiry, 10) - 30000;
}

// Refresh token if needed
async function refreshTokenIfNeeded(): Promise<string | null> {
  const accessToken = getAccessToken();
  const refreshToken = getRefreshToken();

  if (!refreshToken) {
    clearTokens();
    return null;
  }

  // If token is not expired, return it
  if (accessToken && !isTokenExpired()) {
    return accessToken;
  }

  // Try to refresh
  try {
    const response = await fetch(`${IDGOV_CONFIG.backendAuthUrl}/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (!response.ok) {
      clearTokens();
      return null;
    }

    const data = await response.json();
    setTokens(data.data.access_token, data.data.refresh_token, data.data.expires);
    return data.data.access_token;
  } catch {
    clearTokens();
    return null;
  }
}

export function initiateLogin(redirectPath?: string): void {
  const state = generateOAuthState();
  const authState: AuthState = {
    state,
    timestamp: Date.now(),
    redirectPath,
  };

  sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(authState));

  const params = new URLSearchParams({
    response_type: "code",
    client_id: IDGOV_CONFIG.clientId,
    auth_type: IDGOV_CONFIG.authTypes,
    state,
    redirect_uri: IDGOV_CONFIG.redirectUri,
  });

  window.location.href = `${IDGOV_CONFIG.baseUrl}/?${params.toString()}`;
}

export function parseCallbackParams(): OAuthCallbackParams {
  const params = new URLSearchParams(window.location.search);

  return {
    code: params.get("code") ?? undefined,
    state: params.get("state") ?? undefined,
    error: params.get("error") ?? undefined,
    error_description: params.get("error_description") ?? undefined,
  };
}

interface StateValidationResult {
  valid: boolean;
  authState?: AuthState;
  error?: string;
}

export function validateCallbackState(
  receivedState: string
): StateValidationResult {
  const storedStateJson = sessionStorage.getItem(SESSION_STORAGE_KEY);

  if (!storedStateJson) {
    return {
      valid: false,
      error: "Стан авторизації не знайдено. Спробуйте увійти знову.",
    };
  }

  let authState: AuthState;
  try {
    authState = JSON.parse(storedStateJson);
  } catch {
    return { valid: false, error: "Помилка читання стану авторизації." };
  }

  if (!validateOAuthState(receivedState)) {
    return { valid: false, error: "Невірний формат стану авторизації." };
  }

  if (authState.state !== receivedState) {
    return {
      valid: false,
      error: "Стан авторизації не співпадає. Можлива CSRF атака.",
    };
  }

  if (Date.now() - authState.timestamp > AUTH_STATE_MAX_AGE_MS) {
    return {
      valid: false,
      error: "Сесія авторизації закінчилась. Спробуйте знову.",
    };
  }

  sessionStorage.removeItem(SESSION_STORAGE_KEY);

  return { valid: true, authState };
}

export function validateCode(code: string): boolean {
  return IDGOV_VALIDATION.code.test(code);
}

interface ExchangeResult {
  success: boolean;
  user?: IdGovUser;
  redirectPath?: string;
  error?: string;
}

export async function exchangeCodeForSession(
  code: string
): Promise<ExchangeResult> {
  if (!validateCode(code)) {
    return { success: false, error: "Невірний формат коду авторизації." };
  }

  try {
    const response = await fetch(`${IDGOV_CONFIG.backendAuthUrl}/callback`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code }),
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.error_description || data.error || "Помилка авторизації",
      };
    }

    // Store tokens
    if (data.data?.access_token && data.data?.refresh_token) {
      setTokens(data.data.access_token, data.data.refresh_token, data.data.expires);
    }

    return { 
      success: true, 
      user: data.user,
      redirectPath: data.redirectPath 
    };
  } catch {
    return {
      success: false,
      error: "Помилка з'єднання з сервером авторизації.",
    };
  }
}

interface GetUserResult {
  user: IdGovUser | null;
  error?: string;
}

export async function getCurrentUser(): Promise<GetUserResult> {
  try {
    const token = await refreshTokenIfNeeded();
    
    if (!token) {
      return { user: null };
    }

    const response = await fetch(`${IDGOV_CONFIG.backendAuthUrl}/me`, {
      headers: {
        "Authorization": `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        clearTokens();
        return { user: null };
      }
      throw new Error("Failed to get user");
    }

    const data = await response.json();
    return { user: data.user };
  } catch {
    return { user: null, error: "Помилка отримання даних користувача." };
  }
}

export async function logout(): Promise<void> {
  try {
    const token = getAccessToken();
    if (token) {
      await fetch(`${IDGOV_CONFIG.backendAuthUrl}/logout`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
        },
      });
    }
  } catch {
    // Silent fail on logout error
  }

  clearTokens();
  sessionStorage.removeItem(SESSION_STORAGE_KEY);
  window.location.href = "/";
}
